import { useEffect, useState } from "react"
import { useTheme } from "next-themes"
import { Sun, Moon } from "lucide-react"
import { Button } from "@/components/ui/button"
import { SidebarMenuButton } from "@/components/ui/sidebar"

/**
 * Theme toggle (dark ⇄ light). Renders the icon for the theme you'd switch TO.
 * Guards against hydration mismatch with a mounted check so the first paint
 * never disagrees with the resolved client theme.
 *
 * `variant="icon"` (default) → ghost icon button, for the FloatingDock.
 * `variant="sidebar"`        → full-width SidebarMenuButton row with a label,
 *                              matching the other AppSidebar footer controls.
 */
export function ThemeToggle({ variant = "icon" }: { variant?: "icon" | "sidebar" }) {
  const { theme, setTheme } = useTheme()
  const [mounted, setMounted] = useState(false)

  useEffect(() => setMounted(true), [])

  const isDark = mounted ? theme === "dark" : true
  const label = isDark ? "Light mode" : "Dark mode"
  const Icon = isDark ? Sun : Moon
  const toggle = () => setTheme(isDark ? "light" : "dark")

  if (variant === "sidebar") {
    return (
      <SidebarMenuButton
        onClick={toggle}
        tooltip={label}
        className="h-9 text-muted-foreground hover:text-foreground transition-colors"
        aria-label={label}
      >
        <Icon className="size-4" />
        <span className="text-sm">{label}</span>
      </SidebarMenuButton>
    )
  }

  return (
    <Button
      variant="ghost"
      size="icon"
      className="size-9 text-muted-foreground hover:text-foreground"
      onClick={toggle}
      aria-label={label}
      title={label}
    >
      <Icon className="size-4" />
    </Button>
  )
}

export default ThemeToggle
